/* Europe coverage map — vanilla JS for Shopify Dawn section.
   No deps. Inlines a tiny TopoJSON decoder + d3-style equirectangular-to-conic-conformal projection.
   Loads the trimmed europe-50m.json from /assets/.
*/
(function () {
  'use strict';

  /* ---------- tiny TopoJSON feature decoder (subset of topojson-client) ---------- */
  function decodeTopo(topo, name) {
    var obj = topo.objects[name];
    var t = topo.transform;
    var arcs = topo.arcs;

    function decodeArc(idx) {
      var a = arcs[idx < 0 ? ~idx : idx];
      var x = 0, y = 0, out = [];
      for (var i = 0; i < a.length; i++) {
        x += a[i][0]; y += a[i][1];
        out.push([x * t.scale[0] + t.translate[0], y * t.scale[1] + t.translate[1]]);
      }
      if (idx < 0) out.reverse();
      return out;
    }
    function ring(arcsList) {
      var ring = [];
      for (var i = 0; i < arcsList.length; i++) {
        var pts = decodeArc(arcsList[i]);
        if (i > 0) pts = pts.slice(1);
        ring = ring.concat(pts);
      }
      return ring;
    }
    function poly(arcsList) {
      return arcsList.map(ring);
    }

    var feats = obj.geometries.map(function (g) {
      var geom;
      if (g.type === 'Polygon')      geom = { type: 'Polygon',      coordinates: poly(g.arcs) };
      else if (g.type === 'MultiPolygon') geom = { type: 'MultiPolygon', coordinates: g.arcs.map(poly) };
      else geom = null;
      return { type: 'Feature', id: g.id, properties: g.properties || {}, geometry: geom };
    });
    return { type: 'FeatureCollection', features: feats };
  }

  /* ---------- projection: Lambert conformal conic ---------- */
  function deg2rad(d) { return d * Math.PI / 180; }
  function makeLCC(lat0, lat1, lon0, latOrigin) {
    var p1 = deg2rad(lat0), p2 = deg2rad(lat1), p0 = deg2rad(latOrigin);
    var n;
    if (Math.abs(p1 - p2) < 1e-9) n = Math.sin(p1);
    else n = Math.log(Math.cos(p1)/Math.cos(p2)) / Math.log(Math.tan(Math.PI/4+p2/2)/Math.tan(Math.PI/4+p1/2));
    var F = (Math.cos(p1) * Math.pow(Math.tan(Math.PI/4+p1/2), n)) / n;
    var rho0 = F / Math.pow(Math.tan(Math.PI/4+p0/2), n);
    var lam0 = deg2rad(lon0);
    return function (lonlat) {
      var lam = deg2rad(lonlat[0]) - lam0;
      var phi = deg2rad(lonlat[1]);
      var rho = F / Math.pow(Math.tan(Math.PI/4+phi/2), n);
      var theta = n * lam;
      // SVG y is screen-down; negate so north renders up.
      return [rho * Math.sin(theta), -(rho0 - rho * Math.cos(theta))];
    };
  }

  /* ---------- bbox computation (operating-only) for fitExtent ---------- */
  function featureBounds(features) {
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    features.forEach(function (f) {
      if (!f.geometry) return;
      var coords = f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates;
      coords.forEach(function (poly) {
        poly[0].forEach(function (pt) {
          if (pt[0] < minX) minX = pt[0]; if (pt[0] > maxX) maxX = pt[0];
          if (pt[1] < minY) minY = pt[1]; if (pt[1] > maxY) maxY = pt[1];
        });
      });
    });
    return [[minX, minY], [maxX, maxY]];
  }

  /* fitExtent: scale + translate the projection so the operating-country bbox fits inside [pad, w-pad] × [pad, h-pad]. */
  function fitProjection(rawProject, opFeatures, w, h, pad) {
    // First pass: project corners of every operating coordinate, find raw-projected bbox
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    opFeatures.forEach(function (f) {
      if (!f.geometry) return;
      var coords = f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates;
      coords.forEach(function (poly) {
        poly[0].forEach(function (pt) {
          var p = rawProject(pt);
          if (p[0] < minX) minX = p[0]; if (p[0] > maxX) maxX = p[0];
          if (p[1] < minY) minY = p[1]; if (p[1] > maxY) maxY = p[1];
        });
      });
    });
    var k = Math.min((w - 2*pad) / (maxX - minX), (h - 2*pad) / (maxY - minY));
    var tx = (w - k * (minX + maxX)) / 2;
    var ty = (h - k * (minY + maxY)) / 2;
    return function (lonlat) {
      var p = rawProject(lonlat);
      return [k * p[0] + tx, k * p[1] + ty];
    };
  }

  /* ---------- SVG path string from feature ---------- */
  function pathOfFeature(feature, project) {
    if (!feature.geometry) return '';
    var coords = feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates;
    var d = '';
    coords.forEach(function (poly) {
      poly.forEach(function (ring) {
        if (!ring.length) return;
        var first = project(ring[0]);
        d += 'M' + first[0].toFixed(1) + ',' + first[1].toFixed(1);
        for (var i = 1; i < ring.length; i++) {
          var p = project(ring[i]);
          d += 'L' + p[0].toFixed(1) + ',' + p[1].toFixed(1);
        }
        d += 'Z';
      });
    });
    return d;
  }

  /* Centroid (area-weighted) of feature for marker placement. Cheap shoelace on the largest polygon. */
  function centroidOfFeature(feature, project) {
    if (!feature.geometry) return null;
    var coords = feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates;
    var bestArea = 0, bestC = null;
    coords.forEach(function (poly) {
      var ring = poly[0];
      var pts = ring.map(project);
      var a = 0, cx = 0, cy = 0;
      for (var i = 0, n = pts.length; i < n; i++) {
        var p1 = pts[i], p2 = pts[(i+1) % n];
        var f = p1[0]*p2[1] - p2[0]*p1[1];
        a += f; cx += (p1[0] + p2[0]) * f; cy += (p1[1] + p2[1]) * f;
      }
      a *= 0.5;
      if (Math.abs(a) > bestArea) {
        bestArea = Math.abs(a);
        bestC = [cx / (6 * a), cy / (6 * a)];
      }
    });
    return bestC;
  }

  /* ---------- constants ---------- */
  var OPERATING_NUMS = [528, 56, 276, 724, 380]; // NL, BE, DE, ES, IT
  var ISO_BY_NUM = { 528: 'NL', 56: 'BE', 276: 'DE', 724: 'ES', 380: 'IT' };
  var LABEL_POS = {
    NL: { dx: 38, dy: -22, anchor: 'start' },
    BE: { dx: -22, dy: 28,  anchor: 'end' },
    DE: { dx: 0,  dy: 0,   anchor: 'middle' },
    ES: { dx: 0,  dy: 0,   anchor: 'middle' },
    IT: { dx: 14, dy: 8,   anchor: 'start' }
  };
  var VBW = 800, VBH = 700;

  /* ---------- DOM helpers ---------- */
  function el(ns, tag, attrs) {
    var n = ns ? document.createElementNS(ns, tag) : document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (k === 'text') n.textContent = attrs[k];
      else n.setAttribute(k, attrs[k]);
    }
    return n;
  }
  var SVG = 'http://www.w3.org/2000/svg';

  /* ---------- main boot ---------- */
  window.__ecBoot = function (uid) {
    var section = document.querySelector('.ec-' + uid + '[data-ec-section]');
    if (!section) return;
    var assetUrl = section.getAttribute('data-asset-url');
    var dataNode = document.querySelector('script[data-ec-countries-' + uid + ']');
    if (!dataNode) return;
    var COUNTRIES = JSON.parse(dataNode.textContent);

    var svg = section.querySelector('svg.ec-map');
    var listEl = section.querySelector('.ec-list');
    var detailEl = section.querySelector('[data-ec-detail]');

    var state = {
      activeIso: 'NL',
      hoveredIso: null,
      showOthers:  section.getAttribute('data-show-others') === 'true',
      showLabels:  section.getAttribute('data-show-labels') === 'true',
      showLeaders: section.getAttribute('data-show-leaders') === 'true',
      showHalo:    section.getAttribute('data-show-halo') === 'true',
      markerStyle: section.getAttribute('data-marker-style') || 'halo',
      mapBg:       section.getAttribute('data-map-bg') || 'soft',
      homeColor:   section.getAttribute('data-home-color') || '#2E7D5B'
    };

    /* ---- render the country list & detail ---- */
    function renderList() {
      listEl.innerHTML = '';
      COUNTRIES.forEach(function (c) {
        var btn = el(null, 'button', {
          'class': 'ec-row' + (state.activeIso === c.iso ? ' is-active' : '') + (c.iso === 'NL' ? ' is-home' : ''),
          'role': 'tab',
          'data-iso': c.iso
        });
        btn.innerHTML =
          '<span class="ec-name">' + c.name +
          (c.iso === 'NL' ? ' <span class="ec-thuis">Thuis</span>' : '') +
          '</span>' +
          '<span class="ec-since">Sinds ' + c.since + '</span>';
        btn.addEventListener('mouseenter', function () { state.hoveredIso = c.iso; renderMap(); });
        btn.addEventListener('mouseleave', function () { state.hoveredIso = null; renderMap(); });
        btn.addEventListener('click', function () { state.activeIso = c.iso; renderList(); renderDetail(); renderMap(); });
        listEl.appendChild(btn);
      });
    }

    function renderDetail() {
      var c = COUNTRIES.find(function (x) { return x.iso === state.activeIso; });
      if (!c) { detailEl.innerHTML = ''; return; }
      detailEl.innerHTML =
        '<div class="ec-detail-head">' +
          '<span class="ec-flag" aria-hidden="true">' + c.flag + '</span>' +
          '<div>' +
            '<div class="ec-detail-eyebrow">Sinds ' + c.since + '</div>' +
            '<h3>' + c.name + '</h3>' +
          '</div>' +
        '</div>' +
        '<p>' + c.detail + '</p>' +
        '<div class="ec-bullets">' +
          '<div class="ec-bullet">' + iconSVG('truck') + '<div><b>' + c.delivery + '</b></div></div>' +
          '<div class="ec-bullet">' + iconSVG('shield') + '<div><b>' + c.warranty + '</b></div></div>' +
          '<div class="ec-bullet">' + iconSVG('return') + '<div><b>' + c.returns + '</b></div></div>' +
        '</div>';
    }

    function iconSVG(name) {
      if (name === 'truck')  return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2 7h11v9H2zM13 10h5l3 3v3h-8z"/><circle cx="6.5" cy="18" r="1.6"/><circle cx="17.5" cy="18" r="1.6"/></svg>';
      if (name === 'shield') return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z"/></svg>';
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 12a8 8 0 1 0 3-6.2"/><path d="M3 4v5h5"/></svg>';
    }

    /* ---- load TopoJSON & render the map ---- */
    var GEO = null, OP_FEATS = null, OTHER_FEATS = null, project = null;

    fetch(assetUrl)
      .then(function (r) { return r.json(); })
      .then(function (topo) {
        var fc = decodeTopo(topo, 'countries');
        GEO = fc;
        OP_FEATS = fc.features.filter(function (f) { return OPERATING_NUMS.indexOf(+f.id) !== -1; });
        OTHER_FEATS = fc.features.filter(function (f) { return OPERATING_NUMS.indexOf(+f.id) === -1; });
        var raw = makeLCC(35, 65, 10, 52);
        project = fitProjection(raw, OP_FEATS, VBW, VBH, 60);
        renderMap();
      })
      .catch(function (e) {
        section.querySelector('.ec-map-wrap').innerHTML =
          '<div style="padding:48px; text-align:center; color:var(--ec-fg-3)">Kon de kaart niet laden.</div>';
        console.error('europe-coverage:', e);
      });

    function renderMap() {
      if (!project) return;
      var bgG = svg.querySelector('.ec-bg');
      var othersG = svg.querySelector('.ec-others');
      var opG = svg.querySelector('.ec-operating');
      var leadersG = svg.querySelector('.ec-leaders');
      var labelsG = svg.querySelector('.ec-labels');
      var markersG = svg.querySelector('.ec-markers');

      [bgG, othersG, opG, leadersG, labelsG, markersG].forEach(function (g) { while (g.firstChild) g.removeChild(g.firstChild); });

      // Background rect
      var bgFill = state.mapBg === 'white' ? '#FFFFFF'
                 : state.mapBg === 'ink'   ? '#0D0B1A'
                 : state.mapBg === 'none'  ? 'transparent'
                 : '#F6F5F8';
      if (bgFill !== 'transparent') {
        bgG.appendChild(el(SVG, 'rect', { x: 0, y: 0, width: VBW, height: VBH, fill: bgFill }));
      }

      // Other countries
      if (state.showOthers) {
        var onInk = state.mapBg === 'ink';
        var oFill = onInk ? 'rgba(255,255,255,0.06)' : '#E7EBF0';
        var oStroke = onInk ? 'rgba(255,255,255,0.12)' : '#D2D7DE';
        OTHER_FEATS.forEach(function (f) {
          var d = pathOfFeature(f, project);
          if (!d) return;
          othersG.appendChild(el(SVG, 'path', { d: d, fill: oFill, stroke: oStroke, 'stroke-width': 0.4 }));
        });
      }

      // Operating countries
      OP_FEATS.forEach(function (f) {
        var iso = ISO_BY_NUM[+f.id];
        var d = pathOfFeature(f, project);
        if (!d) return;
        var isHome = iso === 'NL';
        var fill = isHome ? state.homeColor : '#1F1B32';
        var p = el(SVG, 'path', {
          d: d, fill: fill, stroke: '#FFFFFF', 'stroke-width': 0.6,
          'data-iso': iso,
          style: 'cursor: pointer; transition: fill 220ms cubic-bezier(0.2,0.8,0.2,1);'
        });
        p.addEventListener('mouseenter', function () { state.hoveredIso = iso; renderMap(); });
        p.addEventListener('mouseleave', function () { state.hoveredIso = null; renderMap(); });
        p.addEventListener('click', function () { state.activeIso = iso; renderList(); renderDetail(); renderMap(); });
        opG.appendChild(p);
      });

      // Compute marker positions (centroids w/ NL nudge)
      var markers = {};
      OP_FEATS.forEach(function (f) {
        var iso = ISO_BY_NUM[+f.id];
        var c = centroidOfFeature(f, project);
        if (!c) return;
        if (iso === 'NL') c[1] -= 8;
        markers[iso] = { x: c[0], y: c[1] };
      });

      // Labels + leader lines
      if (state.showLabels) {
        COUNTRIES.forEach(function (c) {
          var m = markers[c.iso]; if (!m) return;
          var pos = LABEL_POS[c.iso] || { dx: 0, dy: 0, anchor: 'middle' };
          var needsLeader = state.showLeaders && (Math.abs(pos.dx) + Math.abs(pos.dy) > 14);
          if (needsLeader) {
            var inkBg = state.mapBg === 'ink';
            leadersG.appendChild(el(SVG, 'line', {
              x1: m.x, y1: m.y,
              x2: m.x + pos.dx * 0.85, y2: m.y + pos.dy * 0.85,
              stroke: inkBg ? '#FFFFFF' : '#1F1B32',
              'stroke-width': 1, 'stroke-dasharray': '2 2', opacity: 0.55
            }));
          }
          var inkBg2 = state.mapBg === 'ink';
          var t = el(SVG, 'text', {
            x: m.x + pos.dx, y: m.y + pos.dy,
            'text-anchor': pos.anchor || 'middle',
            'font-size': 13, 'font-weight': 800, 'letter-spacing': '0.04em',
            fill: '#FFFFFF',
            style: 'paint-order: stroke; stroke: ' + (inkBg2 ? '#0D0B1A' : '#1F1B32') + '; stroke-width: 3.5px; stroke-linejoin: round; pointer-events: none;'
          });
          t.textContent = c.name.toUpperCase();
          labelsG.appendChild(t);
        });
      }

      // Markers
      if (state.markerStyle !== 'none') {
        COUNTRIES.forEach(function (c, idx) {
          var m = markers[c.iso]; if (!m) return;
          var isHome = c.iso === 'NL';
          var accent = isHome ? state.homeColor : '#E89A5C';
          var g = el(SVG, 'g', {
            transform: 'translate(' + m.x + ',' + m.y + ')',
            style: 'cursor: pointer;',
            'data-iso': c.iso
          });
          g.addEventListener('mouseenter', function () { state.hoveredIso = c.iso; renderMap(); });
          g.addEventListener('mouseleave', function () { state.hoveredIso = null; renderMap(); });
          g.addEventListener('click', function () { state.activeIso = c.iso; renderList(); renderDetail(); renderMap(); });

          var active = state.activeIso === c.iso || state.hoveredIso === c.iso;
          var r1 = active ? 8 : 6, r2 = active ? 4 : 3;

          if (state.markerStyle === 'dot') {
            g.appendChild(el(SVG, 'circle', { r: r2 + 1, fill: accent, stroke: '#FFFFFF', 'stroke-width': 1.5 }));
          } else if (state.markerStyle === 'pin') {
            var R = active ? 9 : 7;
            var pin = el(SVG, 'g', { transform: 'translate(0,' + (-R) + ')' });
            pin.appendChild(el(SVG, 'path', {
              d: 'M0 ' + (R*1.6) + ' L ' + (-R*0.7) + ' ' + (R*0.2) + ' A ' + R + ' ' + R + ' 0 1 1 ' + (R*0.7) + ' ' + (R*0.2) + ' Z',
              fill: accent, stroke: '#1F1B32', 'stroke-width': 1.2
            }));
            pin.appendChild(el(SVG, 'circle', { cy: 0, r: R*0.38, fill: '#FFFFFF' }));
            g.appendChild(pin);
          } else if (state.markerStyle === 'numbered') {
            var R2 = active ? 11 : 9;
            g.appendChild(el(SVG, 'circle', { r: R2, fill: accent, stroke: '#FFFFFF', 'stroke-width': 1.5 }));
            var num = el(SVG, 'text', {
              'text-anchor': 'middle', 'dominant-baseline': 'central', y: 0.5,
              'font-size': 11, 'font-weight': 800, fill: '#FFFFFF'
            });
            num.textContent = String(idx + 1);
            g.appendChild(num);
          } else {
            // halo
            if (state.showHalo) {
              g.appendChild(el(SVG, 'circle', { r: r1, fill: accent, opacity: 0.5, 'class': 'ec-halo', style: 'animation-delay:' + (idx * 0.4) + 's;' }));
            }
            g.appendChild(el(SVG, 'circle', { r: r1, fill: '#FFFFFF', stroke: '#1F1B32', 'stroke-width': 2 }));
            g.appendChild(el(SVG, 'circle', { r: r2, fill: accent }));
          }
          markersG.appendChild(g);
        });
      }
    }

    /* ---- expose a setter so external controls (e.g. Tweaks panel) can drive state ---- */
    section.__ecSetState = function (patch) {
      Object.keys(patch).forEach(function (k) { state[k] = patch[k]; });
      renderList();
      renderDetail();
      renderMap();
    };

    /* ---- initial render ---- */
    renderList();
    renderDetail();
  };
})();
